"use client";

import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "@/components/AppToaster";
import { notifyDonationsChanged } from "@/lib/donationsLive";

type DonationModalProps = {
  onClose: () => void;
};

type PurposeOption = {
  id: number;
  label: string;
};

const gotraList = [
  "Airan (ऐरण)",
  "Bansal (बंसल)",
  "Bhandal (भंदल)",
  "Bindal (बिंदल)",
  "Dharan (धारण)",
  "Garg (गर्ग)",
  "Goyal (गोयल)",
  "Goyan (गोयन)",
  "Jindal (जिंदल)",
  "Kansal (कंसल)",
  "Kuchhal (कुच्छल)",
  "Madhukul (मधुकुल)",
  "Mangal (मंगल)",
  "Mittal (मित्तल)",
  "Nangal (नांगल)",
  "Singhal (सिंघल)",
  "Tayal (तायल)",
  "Tingal (टिंगल)",
];

function todayIso() {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function toProperCase(event: FormEvent<HTMLInputElement>) {
  const input = event.currentTarget;
  input.value = input.value.replace(/\b\w/g, (char) => char.toUpperCase());
}

const screenshotTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
const maxScreenshotBytes = 3 * 1024 * 1024;

function screenshotError(file: File) {
  if (!screenshotTypes.has(file.type)) return "Only JPG, PNG, or WEBP screenshots are allowed.";
  if (file.size > maxScreenshotBytes) return "Screenshot must be 3MB or smaller.";
  return "";
}

function fetchUserDetails(event: FormEvent<HTMLInputElement>) {
  if (event.currentTarget.value.length >= 10) return;
  const name = document.getElementById("modal_donor_name") as HTMLInputElement | null;
  const address = document.getElementById("modal_address") as HTMLInputElement | null;
  const gotra = document.getElementById("modal_gotra") as HTMLSelectElement | null;
  if (name) name.value = "";
  if (address) address.value = "";
  if (gotra) gotra.selectedIndex = 0;
}

export default function DonationModal({ onClose }: DonationModalProps) {
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [purposes, setPurposes] = useState<PurposeOption[]>([]);
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetch("http://localhost:5002/api/donation-purposes")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data?.success) return;
        setPurposes(Array.isArray(data.purposes) ? data.purposes : []);
      })
      .catch(() => {
        if (active) setPurposes([]);
      });
    return () => {
      active = false;
    };
  }, []);

  async function submitDonation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const amount = Number(formData.get("amount"));
    if (!Number.isFinite(amount) || amount < 101) {
      toast.error("Minimum donation amount is ₹101.");
      return;
    }
    const screenshot = formData.get("screenshot");
    if (paymentMode === "UPI" && screenshot instanceof File && screenshot.size > 0) {
      const fileError = screenshotError(screenshot);
      if (fileError) {
        toast.error(fileError);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch("http://localhost:5002/api/donations", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.message || "Donation could not be saved. Please try again.");
        return;
      }
      toast.success("Your donation details have been received.");
      notifyDonationsChanged();
      setSubmitted(true);
    } catch {
      toast.error("Donation could not be saved. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function checkScreenshot(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const fileError = screenshotError(file);
    if (!fileError) return;
    event.currentTarget.value = "";
    toast.error(fileError);
  }

  function closeDonationModal() {
    onClose();
  }

  if (!portalReady) return null;

  return createPortal(
    <>
      <style>{`
        .donation-success { padding: 28px 8px 12px; text-align: center; }
        .donation-success h2 { margin: 0 0 10px; font-family: var(--font-head); font-size: 28px; color: #4A1118; }
        .donation-success p { margin: 0 0 22px; color: #555; font-size: 14px; line-height: 1.6; }
        .avt-qr-section .avt-qr-image { aspect-ratio: 1; background-image: url("/assets/Common/Gpay%20QR.webp"); background-repeat: no-repeat; background-position: center; background-size: contain; background-origin: content-box; pointer-events: none; user-select: none; }
        #donationModal.avt-custom-modal {
          display: flex !important;
          z-index: 200000;
          align-items: flex-start;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: 20px 12px;
        }
      `}</style>
      <div id="donationModal" className="avt-custom-modal" role="dialog" aria-modal="true" aria-label="Donate Now">
        <div className="avt-modal-content" onClick={(event) => event.stopPropagation()}>
          <button type="button" className="avt-modal-close" onClick={closeDonationModal} aria-label="Close">
            &times;
          </button>

          <div className="avt-donate-container">
            <div className="avt-qr-section">
              <h2>Scan to Donate</h2>
              <p className="avt-qr-note">Open GPay, PhonePe, or Paytm and scan this code.</p>
              <div className="avt-qr-image" role="img" aria-label="Donate QR Code" draggable={false} />
              <h3 style={{ margin: "8px 0 0", fontSize: "16px", color: "#4A1118" }}>AVT Kalamboli Trust</h3>
              <p className="avt-qr-note">UPI ID: deepuagarwal5620-3@okaxis</p>
              <p className="avt-qr-note">After payment, fill the form so the trust can verify your donation.</p>
            </div>

            <div className="avt-form-section">
              {submitted ? (
                <div className="donation-success">
                  <h2>Thank You</h2>
                  <p>Your donation details have been received. Agroha Vikas Trust will verify your payment.</p>
                  <button type="button" className="avt-btn-submit" onClick={closeDonationModal}>
                    Close
                  </button>
                </div>
              ) : (
              <>
              <h2 style={{ marginTop: 0, fontSize: "20px", color: "#800000" }}>Submit Payment Details</h2>
              <p style={{ fontSize: "12px", color: "#666", marginBottom: "15px" }}>Please fill in details for verification.</p>

              <form method="POST" encType="multipart/form-data" onSubmit={submitDonation}>
                <input type="hidden" name="submit_donation_modal" value="1" />

                <div className="avt-input-row">
                  <div className="avt-form-group">
                    <label>Mobile Number</label>
                    <input type="tel" name="mobile_no" id="mobile" required maxLength={10} onInput={fetchUserDetails} />
                  </div>
                  <div className="avt-form-group">
                    <label>Gotra (Optional)</label>
                    <select name="gotra" id="modal_gotra" required defaultValue="">
                      <option value="" disabled>
                        -- Choose your Gotra --
                      </option>
                      {gotraList.map((gotra) => (
                        <option key={gotra} value={gotra.split(" ")[0]}>
                          {gotra}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="avt-form-group">
                  <label>Full Name / Company Name</label>
                  <input
                    type="text"
                    name="donor_name"
                    id="modal_donor_name"
                    required
                    placeholder="Name for Receipt"
                    onInput={toProperCase}
                  />
                </div>

                <div className="avt-input-row">
                  <div className="avt-form-group">
                    <label>Address / City</label>
                    <input
                      type="text"
                      name="address"
                      id="modal_address"
                      required
                      placeholder="e.g. Kalamboli"
                      onInput={toProperCase}
                    />
                  </div>
                </div>

                <div className="avt-form-group">
                  <label>Purpose of Donation</label>
                  <select name="occasion" required defaultValue="">
                    <option value="" disabled>
                      Select an Occasion...
                    </option>
                    {purposes.map((item) => (
                      <option key={item.id} value={item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="avt-input-row">
                  <div className="avt-form-group">
                    <label>Donation Date</label>
                    <input type="date" name="donation_date" id="modal_donation_date" required defaultValue={todayIso()} />
                  </div>
                  <div className="avt-form-group">
                    <label>Amount (₹)</label>
                    <input type="number" name="amount" min={101} required placeholder="Min ₹101" />
                  </div>
                </div>

                <div className="avt-form-group">
                  <label>Payment Mode</label>
                  <div style={{ display: "flex", gap: "15px", padding: "5px 0" }}>
                    <label style={{ fontSize: "13px", fontWeight: 500, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="payment_mode"
                        value="UPI"
                        checked={paymentMode === "UPI"}
                        onChange={() => setPaymentMode("UPI")}
                      />{" "}
                      UPI Transfer
                    </label>
                    <label style={{ fontSize: "13px", fontWeight: 500, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="payment_mode"
                        value="Cash"
                        checked={paymentMode === "Cash"}
                        onChange={() => setPaymentMode("Cash")}
                      />{" "}
                      Cash Payment
                    </label>
                  </div>
                </div>

                <div className="avt-form-group" id="modal-screenshot-section" style={{ display: paymentMode === "UPI" ? "block" : "none" }}>
                  <label>Upload Screenshot <span style={{ fontWeight: 500, color: "#8D7568" }}>(Optional)</span></label>
                  <input
                    type="file"
                    name="screenshot"
                    id="modal-screenshot-input"
                    accept="image/jpeg, image/png, image/webp"
                    onChange={checkScreenshot}
                  />
                </div>

                <button type="submit" id="modalSubmitBtn" className="avt-btn-submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Donation"}
                </button>
              </form>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
